import { useEffect, useState } from 'react';
/**
 * Hook to detect scroll state of an element.
 * Returns whether the element has scrollable content and if it's scrolled to the bottom or top.
 *
 * @param contentRef - Ref to the scrollable element
 * @param dependencies - Array of dependencies that should trigger a re-check of scroll state
 * @param enabled - Whether scroll detection should be active (defaults to true)
 * @returns Object with `hasScroll` (boolean), `isAtBottom` (boolean), and `isAtTop` (boolean)
 */ export function useScrollDetection(contentRef) {
    let dependencies = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [], enabled = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : true;
    const [hasScroll, setHasScroll] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [isAtTop, setIsAtTop] = useState(true);
    useEffect(()=>{
        if (!enabled) {
            // Reset to default values when disabled
            setHasScroll(false);
            setIsAtBottom(false);
            setIsAtTop(true);
            return;
        }
        const checkScroll = ()=>{
            if (contentRef.current) {
                const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
                const hasScrollableContent = scrollHeight > clientHeight;
                setHasScroll(hasScrollableContent);
                // Check if scrolled to bottom (with a small threshold for better UX)
                const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 5;
                setIsAtBottom(isScrolledToBottom);
                // Check if scrolled to top (with a small threshold for better UX)
                const isScrolledToTop = scrollTop <= 5;
                setIsAtTop(isScrolledToTop);
            }
        };
        const handleScroll = ()=>{
            checkScroll();
        };
        // Use requestAnimationFrame to ensure DOM is ready when popover opens
        let resizeObserver = null;
        let currentContentRef = null;
        let rafId2 = null;
        let timeoutId = null;
        let listenersSetup = false;
        const setupListeners = ()=>{
            if (listenersSetup) return;
            listenersSetup = true;
            checkScroll();
            resizeObserver = new ResizeObserver(checkScroll);
            currentContentRef = contentRef.current;
            if (currentContentRef) {
                resizeObserver.observe(currentContentRef);
                currentContentRef.addEventListener('scroll', handleScroll);
            }
            window.addEventListener('resize', checkScroll);
        };
        const rafId = requestAnimationFrame(()=>{
            // Double RAF to ensure layout is complete
            rafId2 = requestAnimationFrame(()=>{
                setupListeners();
                // Also add a small timeout as fallback in case RAF fires too early
                timeoutId = setTimeout(setupListeners, 0);
            });
        });
        return ()=>{
            cancelAnimationFrame(rafId);
            if (rafId2 !== null) {
                cancelAnimationFrame(rafId2);
            }
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', checkScroll);
            if (currentContentRef) {
                currentContentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, [
        contentRef,
        enabled,
        ...dependencies
    ]);
    return {
        hasScroll,
        isAtBottom,
        isAtTop
    };
}
